import dayjs from 'dayjs';
import styled from 'styled-components';
import { useState, useMemo } from 'react';
import { mapPlans } from '../utils/mapPlans';
import { Colors } from '../../themes/Colors';
import TextBase from '../text';
import { DealerCopyCode } from '../dealer-code-copy';
import { CreateaccountButton } from '../../modules/child-accounts';
import Modal from '../model';
import PlansGrid from '../subscription-card/PlansGrid';
import { useScreenSubscription } from '../../pages/subscription/screenProviderLocationAccount';
import { WORK_FLOW_COMMON_SCREENS } from '../../pages/subscription/const';
import { useGetPaymentMethods } from '../../hooks/usePayment';
import { useUpdateVendorSubscription, useCreateVendorSubscription } from '../../hooks/useSubscriptions';
import { messageManager, MESSAGE_TYPE, translate } from '../../helper';
import CustomSpinner from '../CustomSpinner';

export const ActiveSubscription = ({ subscriptionData = {}, code = '', status = '', isParentSubscription, hideUpgradeCreateButtons, isLocationLoading = false, setLocationsLoading }) => {
  const [showUpgrade, setShowUpgrade] = useState(false);
  const { subscriptionProduct, setSelectedSubscription, navigateTo, childAccount, triggerRefresh, screen } = useScreenSubscription();
  const { paymentMethods } = useGetPaymentMethods();
  const defaultMethod = (paymentMethods || []).find(pm => pm?.is_default);
  const isPaymentInfo =  screen === WORK_FLOW_COMMON_SCREENS.PAYMENT_INFO ;
  const { updateVendorSubscription, isUpdatingVendorSubscription } = useUpdateVendorSubscription(
    (data) => {
      messageManager({ type: MESSAGE_TYPE.success, messageShow: translate('subscriptionUpdated') || 'Subscription updated successfully' });
      if (typeof triggerRefresh === 'function') triggerRefresh();
      setLocationsLoading && setLocationsLoading(true); // Set loading to true to refetch locations if needed after subscription update
    },
    (err) => {
      messageManager({ type: MESSAGE_TYPE.error, messageShow: err?.error || translate('subscriptionUpdateFailed') || 'Failed to update subscription' });
    }
  );

  const { createVendorSubscription, isCreatingSubscription } = useCreateVendorSubscription(
    (data) => {
      messageManager({ type: MESSAGE_TYPE.success, messageShow: translate('subscriptionCreated') || 'Subscription created successfully' });
      if (typeof triggerRefresh === 'function') triggerRefresh();
      setLocationsLoading && setLocationsLoading(true); // Set loading to true to refetch locations if needed after subscription creation
    },
    (err) => {
      messageManager({ type: MESSAGE_TYPE.error, messageShow: err?.error || translate('subscriptionCreateFailed') || 'Failed to create subscription' });
    }
  );

  const handleUpgradeSelection = (payload) => {
    if (payload) {
      const backend = payload.rawSelected || null;
      const subscriptionId = backend?.id ?? payload.paymentId;
      setSelectedSubscription({
        title: payload.title,
        subTitle: payload.interval === 'year' ? 'yearlySubscription' : 'monthlySubscription',
        paymentId: subscriptionId,
        price: backend?.price ?? payload.price,
        recurringInterval: backend?.recurring_interval ?? payload.interval,
        percentageDiscount: backend?.discount ?? payload.discount,
        isUpgrade: true,
      });
      if (defaultMethod) {
        const updatePayload = { subscription_id: subscriptionId };
        if (!isParentSubscription && childAccount?.id) updatePayload.child_account_id = childAccount.id;
        updateVendorSubscription(updatePayload);
        setShowUpgrade(false);
        return;
      }
    }
    setShowUpgrade(false);
    navigateTo(WORK_FLOW_COMMON_SCREENS.PAYMENT_INFO);
  };

  const handleInitialPurchaseSelection = (payload) => {
    if (payload) {
      const backend = payload.rawSelected || null;
      const subscriptionId = backend?.id ?? payload.paymentId;
      setSelectedSubscription({
        title: payload.title,
        subTitle: payload.interval === 'year' ? 'yearlySubscription' : 'monthlySubscription',
        paymentId: subscriptionId,
        price: backend?.price ?? payload.price,
        recurringInterval: backend?.recurring_interval ?? payload.interval,
        percentageDiscount: backend?.discount ?? payload.discount,
        isUpgrade: false,
      });
      if (defaultMethod) {
        const createPayload = { subscription_id: subscriptionId };
        if (childAccount?.id) createPayload.child_account_id = childAccount.id;
        createVendorSubscription(createPayload);
        setShowUpgrade(false);
        return;
      }
    }
    setShowUpgrade(false);
    navigateTo(WORK_FLOW_COMMON_SCREENS.PAYMENT_INFO);
  };

  const mappedPlans = useMemo(() => mapPlans(subscriptionProduct, subscriptionData, childAccount, isParentSubscription), [subscriptionProduct, subscriptionData, childAccount, isParentSubscription]);
  const isCancelled = subscriptionData?.subscription?.cancel_at_period_end;
  const rawStatus = subscriptionData?.dj_stripe_subscription?.status || status;
  const displayStatus = isCancelled ? 'cancelled' : (rawStatus === 'trialing' ? 'trial' : rawStatus || '');
  const periodEnd = subscriptionData?.dj_stripe_subscription?.current_period_end;
  const amount = subscriptionData?.subscription?.price || subscriptionData?.price;
  const currency = subscriptionData?.plan?.currency || 'USD';

  const amountText = (() => {
    if (amount == null) return '';
    const curr = (currency || '').toUpperCase();
    if (curr === 'USD') return `$${amount}`;
    if (curr) return `${curr} ${amount}`;
    return `${amount}`;
  })();
  const ordersPlaced = subscriptionData?.usage?.order_usage;
  const orderLimit = subscriptionData?.subscription?.free_order_limit;
  const smsSent = subscriptionData?.usage?.sms_usage;
  const smsLimit = subscriptionData?.subscription?.free_sms_limit;
  const additionalOrderCost = subscriptionData?.usage?.order_cost;
  const additionalSmsCost = subscriptionData?.usage?.sms_cost;
  const planTypePeriod = subscriptionData?.subscription?.recurring_interval === 2 ? 'Year' : 'Month';

  const _statusNormalized = (displayStatus || '').toLowerCase();
  const statusType = (
    _statusNormalized === 'pending' ? 'pending' : _statusNormalized === 'paymentpending' ? 'paymentpending' :
    _statusNormalized === 'cancelled' || _statusNormalized === 'canceled' ? 'cancelled' :
    _statusNormalized === 'expired' ? 'expired' :
    'active'
  );

  const exceedOrders = Number(ordersPlaced || 0) > Number(orderLimit || 0);
  const exceedSms = Number(smsSent || 0) > Number(smsLimit || 0);
  return (
    <div style={{ position: 'relative' }}>
      {(isUpdatingVendorSubscription || isCreatingSubscription || isLocationLoading || !subscriptionData) && (
        <CustomSpinner fullPage />
      )}
      <Container>
        <PlanTypeHeader>
          <PlanTypeText>Plan Type</PlanTypeText>
        </PlanTypeHeader>

        <Row>
          <Column>
            <Value>{subscriptionData?.subscription?.name || subscriptionData?.title || 'No Plan'}</Value>
          </Column>
          <RightColumn>
            {!isPaymentInfo && !hideUpgradeCreateButtons && <CreateaccountButton onClick={() => setShowUpgrade(true)}>
              <TextBase style={{ color: Colors.white,textTransform:'capitalize' }} fontWeight={600} size={'calc(var(--fs-xs) * 1.473684)'}>
                {isUpdatingVendorSubscription || isCreatingSubscription
                  ? (subscriptionData?.subscription ? 'Updating...' : 'Creating...')
                  : (subscriptionData?.subscription ? 'Upgrade Plan' : 'Buy Subscription')}
              </TextBase>
            </CreateaccountButton>}
          </RightColumn>
        </Row>

        <Row className="status-row">
          <Column>
            <Label>Status</Label>
            <StatusBadge statusType={statusType}>{translate(displayStatus)}</StatusBadge>
          </Column>
          <RightColumn>
            {!isPaymentInfo && <Label>Active till</Label>}
            {!isPaymentInfo && <Value>{periodEnd ? dayjs(periodEnd).format('MM/DD/YYYY') : 'NA'}</Value>}
          </RightColumn>
        </Row>

        <Row>
          <Column>
            <Label>Amount :</Label>
          </Column>
          <RightColumn>
            <AmountValue>{amountText || '0.00'}/{planTypePeriod}</AmountValue>
          </RightColumn>
        </Row>
      </Container>

      {!isPaymentInfo && <StatsGrid>
        <SmallCard>
          <SmallLabel>Orders placed so far :</SmallLabel>
          <SmallValue highlight={exceedOrders}>{subscriptionData?.subscription ? `${ordersPlaced}/${orderLimit}` : 'NA'}</SmallValue>
        </SmallCard>

        <SmallCard>
          <SmallLabel>SMS sent till date :</SmallLabel>
          <SmallValue highlight={exceedSms}>{subscriptionData?.subscription ? `${smsSent}/${smsLimit}` : 'NA'}</SmallValue>
        </SmallCard>

        <SmallCard>
          <SmallLabel>Additional order cost :</SmallLabel>
          <SmallValue>{subscriptionData?.subscription ? additionalOrderCost : 'NA'}</SmallValue>
        </SmallCard>

        <SmallCard>
          <SmallLabel>Additional messages cost :</SmallLabel>
          <SmallValue>{subscriptionData?.subscription ? additionalSmsCost : 'NA'}</SmallValue>
        </SmallCard>
      </StatsGrid>}

      {code && (
        <DealerCodeWrapper>
          <TextBase style={{ marginBottom: 'var(--sp-md2)' }} fontWeight={600} size={'var(--fs-md2)'} color={Colors.primary}>
            Dealer Unique Code
          </TextBase>
          <DealerCopyCode value={code} btnText={'Copy'} onClick={() => {}} />
        </DealerCodeWrapper>
      )}

      <Modal
        title={'Plans for every business and budget.'}
        isOpen={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        minWidth="calc(var(--fs-hero) * 12.150943)"
        padding="var(--sp-lg) var(--sp-xl)"
        weight={500}
      >
        <div style={{ overflow: 'visible', position: 'relative', width: '100%' }}>
          {mappedPlans && mappedPlans.length > 0 ? (
            <PlansGrid
              headerTitle={''}
              plans={mappedPlans.filter(p => (p.key || '').toLowerCase() !== 'free trial')}
              initialIsYearly={(subscriptionData?.subscription?.recurring_interval || 1) === 2}
              currentPlanKey={(subscriptionData?.subscription?.name || '').toLowerCase().trim()}
              buttonText1={'Cancel'}
              buttonText2={subscriptionData?.subscription ? 'Upgrade' : 'Buy'}
              buttonsRowReverse
              onButton2Click={subscriptionData?.subscription ? handleUpgradeSelection : handleInitialPurchaseSelection}
              onButton1Click={() => setShowUpgrade(false)}
            />
          ) : (
            <div style={{ padding: 'calc(var(--sp-md2) * 1.132075) var(--sp-xxs)' }}>
              <TextBase fontWeight={500} size={'calc(var(--fs-xs) * 1.32075)'} color={Colors.black} style={{ textAlign: 'center' }}>
                You are already on the highest available plan.
              </TextBase>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

const Container = styled.div`
  padding: var(--sp-lg) var(--sp-xl);
  width: 100%;
  border-radius: calc(var(--sp-xxs) * 2);
  box-shadow: 0 0 2px 0 rgba(0, 0, 0, 0.25);
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  align-items: center;
  gap: calc(var(--sp-lg) * 1.4150943);
  margin-bottom: calc(var(--sp-md2) * 1.50943);
  border-bottom: 1px solid ${Colors.gray300};
  padding-bottom: calc(var(--sp-md2) * 1.4150943);
  &.status-row {
    padding-bottom: calc(var(--sp-lg) * 1.4150943);
  }
  &:last-of-type {
    border-bottom: none;
    padding-bottom: 0;
    margin-bottom: 0;
  }
`;

const Column = styled.div`
  min-width: calc(var(--fs-hero) * 4.71698);
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: calc(var(--sp-md2) * 1.132075);
`;

const RightColumn = styled(Column)`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const Label = styled(TextBase).attrs(() => ({
  fontWeight: 400,
  size: 'calc(var(--fs-xs) * 1.368421)',
  color: 'rgba(64, 64, 64, 1)',
}))`
  letter-spacing: calc(var(--sp-xxs) * 0.07075);
  opacity: 1;
`;

const Value = styled(TextBase).attrs(() => ({
  fontWeight: 500,
  size: 'calc(var(--fs-xs) * 1.578947)',
  color: Colors.black,
  marginTop: 'var(--sp-sm)'
}))`
  
`;

const AmountValue = styled(TextBase).attrs(() => ({
  fontWeight: 500,
  size: 'calc(var(--fs-xl) * 1.2631578)',
  color: Colors.black,
}))`
  
`;

const StatusBadge = styled.div`
  display: inline-flex;
  padding: var(--sp-xxs) var(--sp-md2);
  border-radius: var(--sp-sm);
  width: fit-content;
  background: ${({ statusType }) => (
    statusType === 'pending' ? 'rgba(255, 193, 7, 1)' :
    statusType === 'paymentpending' ? 'rgba(255, 193, 7, 1)' :
    statusType === 'cancelled' ? 'rgba(220, 53, 69, 1)' :
    statusType === 'expired' ? 'rgba(108, 117, 125, 1)' :
    'rgba(61, 183, 116, 1)'
  )};
  color: ${Colors.white};
  font-weight: 600;
  font-size: calc(var(--fs-xs) * 1.368421);
  line-height: 1.2;
  text-transform: capitalize;
  letter-spacing: calc(var(--sp-xxs) * 0.07075);
  box-shadow: 0 1px 2px rgba(0,0,0,0.12);
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: calc(var(--sp-md2) * 1.132075);
  margin-top: calc(var(--sp-md2) * 1.132075);
`;

const SmallCard = styled.div`
  background: ${Colors.white};
  border-radius: var(--sp-md2);
  padding: var(--sp-lg) calc(var(--sp-md2) * 1.4150943);
  box-shadow: 0 0 2px 0 rgba(0, 0, 0, 0.25);

  min-height: calc(var(--fs-hero) * 1.698);
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: var(--sp-md2);
`;

const SmallLabel = styled(TextBase).attrs(({ highlight }) => ({
  size: 'calc(var(--fs-xs) * 1.368421)',
  color: highlight ? Colors.red : 'rgba(64, 64, 64, 1)',
  fontWeight: 400,
}))`
  margin-bottom: var(--sp-sm);
`;

const SmallValue = styled(TextBase).attrs(({ highlight }) => ({
  size: 'calc(var(--fs-xs) * 1.473684)',
  color: highlight ? 'rgba(228, 78, 78, 1)' : Colors.black,
  fontWeight: 500,
}))``;

const DealerCodeWrapper = styled.div`
  margin-top: calc(var(--sp-lg) * 1.32075);
  padding: calc(var(--sp-md2) * 1.50943) calc(var(--sp-md2) * 1.69811);
  border: 1px dashed ${Colors.primary};
  border-radius: var(--sp-md2);
  background: ${Colors.mainColor_14};
`;

const PlanTypeHeader = styled.div`
  background: ${Colors.greyBg};
  padding: calc(var(--sp-md2) * 1.50943) 0  calc(var(--sp-md2) * 1.32075) 0;
  margin: calc(var(--sp-md2) * -1.8867) calc(var(--sp-lg) * -1.132075) calc(var(--sp-md2) * 1.50943) calc(var(--sp-lg) * -1.132075); /* extend to container edges */
  padding-left: calc(var(--sp-lg) * 1.132075);
  border-bottom: 1px solid ${Colors.gray300};
  border-top-left-radius: calc(var(--sp-xxs) * 2);
  border-top-right-radius: calc(var(--sp-xxs) * 2);
`;

const PlanTypeText = styled(TextBase).attrs(() => ({
  fontWeight: 400,
  size: 'calc(var(--fs-xs) * 1.368421)',
  color: Colors.black,
}))``;
